import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderDash from "../../components/HeaderDash";
import { Languages, MapPin, Search } from "lucide-react";
import FeaturedDestination from "../../components/FeaturedDestination";
import assistantsService, {
  normalizeAssistantListItem,
} from "../../api/services/assistantsService";
import DestinationCombobox from "../../components/DestinationCombobox";
import {
  NIGERIA_STATES as stateOptions,
  getStateLabel,
} from "../../constants/nigeriaStates";

function TravelAssistant() {
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const assistantSectionRef = useRef(null);

  useEffect(() => {
    async function fetchAssistants() {
      try {
        const res = await assistantsService.getAssistants();
        const results =
          res?.data?.items ||
          res?.data?.results ||
          res?.data ||
          res?.results ||
          (Array.isArray(res) ? res : []);
        if (Array.isArray(results)) {
          setAssistants(results.map(normalizeAssistantListItem));
        }
      } catch (error) {
        console.error("Error fetching travel assistants:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAssistants();
  }, []);

  const extractResults = (res) =>
    res?.data?.items ||
    res?.data?.results ||
    res?.items ||
    res?.results ||
    res?.data ||
    (Array.isArray(res) ? res : []);

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Request timed out. Please try again.")),
          ms,
        ),
      ),
    ]);

  useEffect(() => {
    let active = true;

    const fetchAssistants = async () => {
      try {
        const res = await withTimeout(assistantsService.getAssistants(), 15000);
        const results = extractResults(res);
        if (!active) return;
        if (Array.isArray(results)) {
          setAssistants(results.map(normalizeAssistantListItem));
        } else {
          setAssistants([]);
        }
      } catch (error) {
        if (!active) return;
        setAssistants([]);
        console.error("Error fetching travel assistants:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchAssistants();
    return () => {
      active = false;
    };
  }, []);

  const [selectedState, setSelectedState] = useState(null);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = selectedState ? { state: selectedState } : {};
      const res = await withTimeout(
        assistantsService.getAssistants(params),
        15000,
      );
      const results = extractResults(res);
      if (Array.isArray(results)) {
        setAssistants(results.map(normalizeAssistantListItem));
      } else {
        setAssistants([]);
      }
    } catch (error) {
      setAssistants([]);
      console.error("Error fetching travel assistants:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    handleSearch();
  };

  const formatCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "0";
    return parsed.toLocaleString();
  };
  //END NEW

  return (
    <div className="min-h-screen bg-[#f8f7f9]">
      <HeaderDash />
      <link rel="stylesheet" href="https://fonts.googleapis.com" />

      <main className="w-[95%] mx-auto space-y-10 pb-10 pt-8">
        <section className="w-[96%] mx-auto">
          {/* Mobile */}
          <div className="sm:hidden max-w-xl mx-auto">
            <form
              onSubmit={handleSearchSubmit}
              className="rounded-3xl border border-white/60 bg-white/90 p-5 shadow-lg backdrop-blur-sm"
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-800 font-semibold">
                    Destination
                  </label>

                  <DestinationCombobox
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    options={stateOptions}
                    getOptionLabel={getStateLabel}
                    inputClassName="w-full rounded-2xl border border-[#b8c1d0] bg-white px-4 py-3 text-base font-medium outline-none focus:border-[#570033] focus:ring-2 focus:ring-[#570033]/20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#570033] px-5 py-3 text-base font-semibold text-white shadow-md transition-colors hover:bg-[#6f0044] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <span className="flex items-center justify-center gap-2">
                    Search
                    <Search className="h-4 w-4" />
                  </span>
                </button>
              </div>
            </form>
          </div>

          {/* Desktop */}
          <div className="hidden sm:block relative rounded-xl shadow-lg bg-white">
            <div className="relative overflow-hidden rounded-xl">
              <img
                src="/images/travelassist.png"
                alt="Travel Assistant"
                className="w-full h-[340px] md:h-[430px] object-cover"
              />
              <div className="absolute inset-0 rounded-xl bg-black/35" />
            </div>

            <div className="absolute max-w-lg m-auto left-4 right-4 md:left-6 md:right-6 top-20">
              <h2 className="text-[3.01rem] text-white text-center font-bold">
                Explore The World With Pulo Assistants
              </h2>
              <p className="text-white font-medium text-center text-lg">
                Find trusted Pulo assistants who know the city like home
              </p>
            </div>

            <div className="absolute max-w-xl m-auto left-4 right-4 md:left-6 md:right-6 bottom-6">
              <form
                onSubmit={handleSearchSubmit}
                className="flex flex-col gap-4 rounded-3xl border border-white/60 bg-white/50 p-5 shadow-xl backdrop-blur-sm sm:flex-row sm:items-end"
              >
                <div className="flex flex-1 flex-col gap-2">
                  <span className="text-sm font-semibold text-gray-700">
                    Destination
                  </span>

                  <DestinationCombobox
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    options={stateOptions}
                    getOptionLabel={getStateLabel}
                    inputClassName="w-full rounded-2xl border border-[#b8c1d0] bg-white px-4 py-3 text-base font-medium outline-none focus:border-[#570033] focus:ring-2 focus:ring-[#570033]/20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-2xl bg-[#570033] px-8 py-3 text-base font-semibold text-white shadow-md transition-colors hover:bg-[#6f0044] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <span className="flex items-center justify-center gap-2">
                    Search
                    <Search className="h-4 w-4" />
                  </span>
                </button>
              </form>
            </div>
          </div>
        </section>
        {/* <FilterDashboard /> */}
        <FeaturedDestination />

        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#570033]">
                Meet Our Pulo Assistants
              </h2>
              <p className="text-sm text-slate-500">
                Trusted travel companions curated just for you.
              </p>
            </div>
            <button
              className="text-sm font-medium text-[#570033] hover:text-[#6f0044]"
              onClick={() => navigate("/explore")}
            >
              See more
            </button>
          </div>

          {loading ? (
            <div className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`assistant-wait-${index}`}
                  className="animate-pulse rounded-xl border border-gray-200 bg-white p-[6px] shadow-sm"
                >
                  <div className="h-48 w-full rounded-xl bg-gray-200" />
                  <div className="px-1 py-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="h-4 w-28 rounded bg-gray-200" />
                        <div className="h-3 w-20 rounded bg-gray-200" />
                      </div>
                      <div className="h-8 w-8 rounded-full bg-gray-200" />
                    </div>
                    <div className="h-3 w-32 rounded bg-gray-200" />
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-24 rounded bg-gray-200" />
                      <div className="h-3 w-10 rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : assistants.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm font-medium text-gray-500">
              No Assistants Found
            </div>
          ) : (
            <div
              ref={assistantSectionRef}
              className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {assistants.map((assistant) => (
                <article
                  key={assistant.id}
                  onClick={() =>
                    navigate(`/assistant/${assistant.id}`, {
                      state: { assistant },
                    })
                  }
                  className="bg-[#F3F3F31D] rounded-xl border border-gray-200 shadow-sm p-[6px] cursor-pointer transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <img
                    src={assistant.image}
                    alt={assistant.name}
                    className="w-full h-60 rounded-xl object-cover object-top"
                  />
                  <div className="pt-4 space-y-2">
                    <div className="space-y-1">
                      <p className="text-lg font-semibold text-black">
                        {assistant.name}
                      </p>
                      <div className="text-sm font-medium text-black">
                        {formatCount(assistant.completedBookings)} completed
                        bookings
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-xs font-medium text-black">
                      <MapPin className="w-4 h-4 text-black" />
                      <span>
                        {assistant.location || "Location unavailable"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1 text-xs font-medium text-black">
                        <Languages className="w-4 h-4 text-black" />
                        <span>
                          {typeof assistant.languages === "string"
                            ? assistant.languages
                            : Array.isArray(assistant.languages)
                              ? assistant.languages.join(", ")
                              : assistant.languages?.join?.(", ")}
                        </span>
                      </div>

                      <div className="flex items-center pt-2 text-xs font-medium">
                        <button
                          className="text-xs font-medium text-[#570033] underline underline-offset-4"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/assistant/${assistant.id}`, {
                              state: { assistant },
                            });
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* <NeedTour /> */}

        <section className="mx-auto flex flex-col gap-4 rounded-2xl bg-[#570033] p-6 text-white shadow-xl md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-purple-200">
              Contact Us
            </p>
            <h3 className="text-2xl font-semibold">
              Need help fine-tuning a trip?
            </h3>
            <p className="text-sm text-purple-100">
              Our travel specialists are standing by to pair you with the best
              assistant for your destination.
            </p>
          </div>
          <button
            className="rounded-full bg-white px-5 py-2 text-[#570033] font-semibold shadow-md hover:bg-gray-100 transition"
            onClick={() => navigate("/user/contact")}
          >
            Contact Us
          </button>
        </section>
      </main>
    </div>
  );
}

export default TravelAssistant;
